import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Lock, Mail } from "lucide-react";

function formatApiError(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login, admin } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (admin) {
    navigate("/admin", { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-soft px-4" data-testid="page-admin-login">
      <div className="w-full max-w-md bg-white border border-slate-100 rounded-[24px] p-8 md:p-10 shadow-soft">
        <div className="flex flex-col items-center text-center">
          <img src="/logo.jpg" alt="ENCES Global" className="w-16 h-16 rounded-2xl object-cover" />
          <h1 className="mt-5 text-2xl font-extrabold text-navy">Admin Panel</h1>
          <p className="mt-1 text-slate-600 text-sm">Sign in to manage leads and applications</p>
        </div>

        {error && (
          <div className="mt-6 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium border border-red-100" data-testid="admin-login-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <Label htmlFor="al-email" className="flex items-center gap-1.5"><Mail size={14} /> Email</Label>
            <Input id="al-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl" data-testid="admin-login-email" />
          </div>
          <div>
            <Label htmlFor="al-pw" className="flex items-center gap-1.5"><Lock size={14} /> Password</Label>
            <Input id="al-pw" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl" data-testid="admin-login-password" />
          </div>
          <button type="submit" disabled={submitting} className="w-full px-6 py-3.5 rounded-full bg-navy hover:bg-red-700 text-white font-semibold disabled:opacity-60" data-testid="admin-login-submit">
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <a href="/" className="block text-center text-xs text-slate-500 mt-6 hover:text-navy">← Back to website</a>
      </div>
    </div>
  );
}
